import { useEffect, useMemo, useState } from 'react';
import { ExtraBase, QuoteExtraApiModel } from '~/lib/api/smartsend/types';
import { UniquIdList, onlyUnique } from '../../lib/helpers/ArrayHelper';
import AlertWithIcon from '../AlertWithIcon';
import { DeliveredDutyPaidExtra } from '../ExtrasHelper';
import Loading from '../../../../components/loading/Loading';
import { AdditionalCustomsNotice } from './AdditionalCustomsNotice';
import { EditType } from './item/EditType';
import { IossIsPrePaid } from '../../lib/helpers/IossUtils';
import {
  P2GBox,
  P2GText,
  P2GRadio,
  P2GLink,
  P2GFade,
  P2GTextField,
  SvgConfirmCircle,
  SvgEnquiryMoreInfo
} from '@parcel2go/core-ui';
import { toDisplayCurrency } from '~/components/currency/toDisplayCurrency';

interface props {
  ddpQuoteKey: string;
  dduUnavailable: boolean;
  iossNumberEnabled: boolean;
  iossNumberRequired: boolean;
  iossNumberValidation: string;
  iossNumber: string;
  iossNumberSource: string;
  availableExtras: QuoteExtraApiModel[];
  extras: ExtraBase[];
  canGetDdpCalculation: boolean;
  getDdpCost: () => Promise<{
    DutyValue: number;
    VatValue: number;
    AdminFee?: number;
  }>;
  onIossUpdate: (v: string) => void;
  toggleExtra: (extra: ExtraBase, include: boolean) => void;
  changeTab: (action: EditType) => void;
  recipientIsoCode: string;
  currencyCode: string;
  wrapper: (children: React.ReactNode) => JSX.Element;
}

type dutyOption = 'DDU' | 'IOSS' | 'DDP';

export const CustomsDuties: React.FC<props> = (props) => {
  const p = { pl: 4, mt: 1, mb: 2 };
  const [ddpCalculation, setDdpCalculation] = useState<{ DutyValue: number; VatValue: number; AdminFee?: number }>();

  const [selected, setSelected] = useState<dutyOption>('IOSS');
  const ddpExtra: QuoteExtraApiModel = useMemo(
    () => UniquIdList(props.extras, props.availableExtras).find((x) => x.Id === DeliveredDutyPaidExtra),
    [props.extras, props.availableExtras]
  );

  const ddpSelected = useMemo(() => !!props.extras.find((x) => x.Id === DeliveredDutyPaidExtra), [props.extras]);

  const options: dutyOption[] = useMemo(() => {
    const iossPrePaid = IossIsPrePaid(props.iossNumberSource);
    const ops: dutyOption[] = [];
    if (props.iossNumberEnabled) {
      ops.push('IOSS');
      if (!iossPrePaid && !props.dduUnavailable) {
        ops.push('DDU');
      }
    }
    if (ddpExtra && !iossPrePaid) {
      ops.push('DDP');
      if (!props.dduUnavailable) {
        ops.push('DDU');
      }
    }
    return ops.filter(onlyUnique);
  }, [props.iossNumberSource, props.iossNumberEnabled, props.dduUnavailable, ddpExtra]);

  useEffect(() => {
    if (options.length > 0) {
      if (props.iossNumber && options.includes('IOSS')) {
        setSelected('IOSS');
      } else {
        if (options.includes('DDP') && ddpSelected) {
          setSelected('DDP');
        } else {
          if (options.includes('DDU')) {
            setSelected('DDU');
          }
        }
      }

      if (options.length === 1) {
        setSelected(options[0]);
      }
    }
  }, [ddpSelected, options, props.iossNumber]);

  useEffect(() => {
    if (selected === 'DDP') {
      if (props.canGetDdpCalculation) {
        props.getDdpCost().then(setDdpCalculation);
      }
      props.toggleExtra(ddpExtra, true);
    } else {
      props.toggleExtra(ddpExtra, false);
    }
  }, [selected, props.ddpQuoteKey]);

  if (!options || options.length == 0) {
    return null;
  }

  return props.wrapper(
    <P2GBox>
      <P2GText variant="body2">Please choose one of the following options:</P2GText>
      {options.includes('IOSS') && (
        <P2GBox>
          <P2GRadio
            id="Radio_Ioss"
            name="CUSTOMS"
            checked={selected === 'IOSS'}
            value="IOSS"
            onChange={() => setSelected('IOSS')}
            label={
              <P2GBox>
                I'm using <P2GLink>Import One Stop Shop (IOSS)</P2GLink>.
              </P2GBox>
            }
          />
          {selected === 'IOSS' && (
            <P2GFade in>
              <P2GBox display="flex" flexDirection="column" gap={2} {...p}>
                <P2GTextField
                  fullWidth
                  value={props.iossNumber}
                  pattern={props.iossNumberValidation as any}
                  id="IossNumber"
                  showErrorsWhenPristine={true}
                  label="IOSS Number"
                  onChange={(v) => props.onIossUpdate(v.currentTarget.value)}
                  required={props.iossNumberRequired}
                  disabled={['Api', 'Defaults'].includes(props.iossNumberSource)}
                />
                <AdditionalCustomsNotice recipientIsoCode={props.recipientIsoCode} />
              </P2GBox>
            </P2GFade>
          )}
        </P2GBox>
      )}

      {options.includes('DDP') && (
        <P2GBox>
          <P2GRadio
            id="Radio_Ddp"
            name="CUSTOMS"
            checked={selected === 'DDP'}
            value="DDP"
            onChange={() => setSelected('DDP')}
            label={<P2GBox>Yes, I'll pay the taxes and duties.</P2GBox>}
          />
          {selected === 'DDP' && (
            <P2GFade in>
              <P2GBox {...p}>
                {props.canGetDdpCalculation ? (
                  <>
                    {ddpCalculation ? (
                      <P2GBox display="flex" flexDirection="column" gap={2}>
                        <AlertWithIcon icon={<SvgConfirmCircle />} color="success">
                          <P2GBox mb={1}>
                            <P2GText variant="body2">
                              The following charge of:{' '}
                              {toDisplayCurrency(
                                props.currencyCode,
                                ddpCalculation.DutyValue +
                                  ddpCalculation.VatValue +
                                  (ddpCalculation.AdminFee ?? (ddpExtra?.Price || 0))
                              )}{' '}
                              has been applied to your order.
                            </P2GText>
                          </P2GBox>
                          <P2GBox mb={1}>
                            <P2GText variant="body2">
                              The charge above is based on your contents, tariff code and the value you have declared.
                              Any misdeclaration may result in further charges from customs.
                            </P2GText>
                          </P2GBox>
                          {ddpExtra && (
                            <P2GBox>
                              <P2GText variant="body2">
                                The price above includes an Admin Fee of{' '}
                                {toDisplayCurrency(props.currencyCode, ddpCalculation.AdminFee ?? ddpExtra.Price)}.
                              </P2GText>
                            </P2GBox>
                          )}
                        </AlertWithIcon>
                        <AdditionalCustomsNotice recipientIsoCode={props.recipientIsoCode} />
                      </P2GBox>
                    ) : (
                      <Loading />
                    )}
                  </>
                ) : (
                  <AlertWithIcon>
                    <P2GText variant="body2">
                      Just need to make sure we have all the{' '}
                      <P2GLink onClick={() => props.changeTab('CONTENTS')}>tariff codes</P2GLink> filled in first.
                    </P2GText>
                  </AlertWithIcon>
                )}
              </P2GBox>
            </P2GFade>
          )}
        </P2GBox>
      )}

      {options.includes('DDU') && (
        <P2GBox>
          <P2GRadio
            id="Radio_DDU"
            name="CUSTOMS"
            checked={selected === 'DDU'}
            value="DDU"
            onChange={() => setSelected('DDU')}
            label={<P2GBox>No, the recipient will pay any taxes and duties, if applicable.</P2GBox>}
          />
          {selected === 'DDU' && (
            <P2GFade in>
              <P2GBox {...p}>
                <AlertWithIcon icon={<SvgEnquiryMoreInfo />}>
                  <P2GText variant="body2">
                    Paying for any taxes and duties before your parcel is sent will significantly reduce delays and
                    increase efficient customs clearance.
                  </P2GText>
                </AlertWithIcon>
              </P2GBox>
            </P2GFade>
          )}
        </P2GBox>
      )}
    </P2GBox>
  );
};
